// ============================================================================
// CLEANING TASKS HOOK (Owner view)
// Fetches tasks for a specific property with reservations and checklists
// ============================================================================

import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { 
  CleaningTaskWithRelations, 
  Reservation, 
  ChecklistRule,
  StayLengthValue,
  GapDaysValue,
  DayOfWeekValue,
} from "@/types/cleaning";
import { scheduleNextPreventativeMaintenanceOnComplete } from "@/lib/scheduleNextPreventativeMaintenance";

export type { CleaningTaskWithRelations };

// Helper to calculate nights between two dates
const calculateNights = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = end.getTime() - start.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Helper to get day of week name from date
const getDayOfWeek = (dateStr: string): string => {
  const date = new Date(dateStr);
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return days[date.getDay()];
};

// Helper to calculate gap days between this checkout and next checkin
const calculateGapDays = (
  currentEndDate: string,
  allReservations: Reservation[]
): number | null => {
  const currentEnd = new Date(currentEndDate);
  
  // Find the next reservation that starts after or on this checkout
  const nextReservations = allReservations
    .filter(r => new Date(r.start_date) >= currentEnd)
    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
  
  if (nextReservations.length === 0) return null;
  
  const nextStart = new Date(nextReservations[0].start_date);
  const diffTime = nextStart.getTime() - currentEnd.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Evaluate a single rule against reservation data
const evaluateRule = (
  rule: ChecklistRule,
  stayLength: number,
  gapDays: number | null,
  checkoutDayOfWeek: string
): boolean => {
  const value = rule.rule_value as Record<string, unknown>;
  
  switch (rule.rule_type) {
    case "stay_length": {
      const stayValue = value as unknown as StayLengthValue;
      return stayLength >= stayValue.min_nights && stayLength <= stayValue.max_nights;
    }
    case "same_day_turnover": {
      return gapDays === 0;
    }
    case "gap_days": {
      const gapValue = value as unknown as GapDaysValue;
      return gapDays === gapValue.gap_days;
    }
    case "day_of_week": {
      const dayValue = value as unknown as DayOfWeekValue;
      return dayValue.weekdays.includes(checkoutDayOfWeek);
    }
    default:
      return false;
  }
};

// Helper to format rule reason
const formatRuleReason = (rule: ChecklistRule): string => {
  const value = rule.rule_value as Record<string, unknown>;
  
  switch (rule.rule_type) {
    case "stay_length": {
      const stayValue = value as unknown as StayLengthValue;
      return `Stay length ${stayValue.min_nights}–${stayValue.max_nights} nights rule`;
    }
    case "same_day_turnover": {
      return "Same-day turnover rule";
    }
    case "gap_days": {
      const gapValue = value as unknown as GapDaysValue;
      return gapValue.gap_days === 0 ? "No gap between bookings rule" : `${gapValue.gap_days} day gap rule`;
    }
    case "day_of_week": {
      const dayValue = value as unknown as DayOfWeekValue;
      return `Day of week (${dayValue.weekdays.join(", ")}) rule`;
    }
    default:
      return "Automation rule";
  }
};

/** Columns we select for cleaning_tasks to avoid 400 (schema-safe, no phantom columns). */
const CLEANING_TASKS_SELECT =
  "id, property_id, reservation_id, cleaner_id, assignee_user_id, coordinator_user_id, checklist_id, status, scheduled_date, scheduled_start_at, scheduled_end_at, override_check_in_time, override_check_out_time, assigned_type, assignment_type, assignment_reason, notes, is_test, created_at, started_at, completed_at, duration_minutes, start_location_lat, start_location_lng, start_location_accuracy_m, start_location_distance_m, start_location_verified, start_location_override_reason, start_location_captured_at, entry_walkthrough_state, checklists(id, name)";

/** Fallback if assigned_type or scheduled_* columns missing in DB (avoids 400). */
const CLEANING_TASKS_SELECT_MINIMAL =
  "id, property_id, reservation_id, cleaner_id, assignee_user_id, coordinator_user_id, checklist_id, status, scheduled_date, override_check_in_time, override_check_out_time, assignment_type, assignment_reason, notes, is_test, created_at, started_at, completed_at, duration_minutes, start_location_lat, start_location_lng, start_location_accuracy_m, start_location_distance_m, start_location_verified, start_location_override_reason, start_location_captured_at, entry_walkthrough_state, checklists(id, name)";

/** No embed (avoids 400/406 when RLS or embed not allowed). */
const CLEANING_TASKS_SELECT_NO_EMBED =
  "id, property_id, reservation_id, cleaner_id, assignee_user_id, coordinator_user_id, checklist_id, status, scheduled_date, scheduled_start_at, scheduled_end_at, override_check_in_time, override_check_out_time, assigned_type, assignment_type, assignment_reason, notes, is_test, created_at, started_at, completed_at, duration_minutes, start_location_lat, start_location_lng, start_location_accuracy_m, start_location_distance_m, start_location_verified, start_location_override_reason, start_location_captured_at, entry_walkthrough_state";

export const useCleaningTasks = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["cleaning-tasks", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const run = (select: string) =>
        supabase
          .from("cleaning_tasks")
          .select(select)
          .eq("property_id", propertyId)
          .order("scheduled_date", { ascending: true });

      let result = await run(CLEANING_TASKS_SELECT);
      if (result.error && (result.error.code === "400" || result.error.code === "406" || (result.error as { status?: number }).status === 406)) {
        result = await run(CLEANING_TASKS_SELECT_MINIMAL);
      }
      if (result.error && (result.error.code === "400" || result.error.code === "406" || (result.error as { status?: number }).status === 406)) {
        result = await run(CLEANING_TASKS_SELECT_NO_EMBED);
      }
      if (result.error) throw result.error;
      let data = (result.data ?? []) as (CleaningTaskWithRelations & { checklist_id?: string | null })[];
      if (data.length > 0 && !data[0].checklists) {
        const checklistIds = [...new Set(data.map((t) => t.checklist_id).filter(Boolean))] as string[];
        const { data: checklistRows } = checklistIds.length
          ? await supabase.from("checklists").select("id, name").in("id", checklistIds)
          : { data: [] };
        const checklistMap = new Map((checklistRows ?? []).map((c) => [c.id, c]));
        data = data.map((t) => ({
          ...t,
          checklists: t.checklist_id ? checklistMap.get(t.checklist_id) ?? null : null,
        })) as CleaningTaskWithRelations[];
      }
      return data;
    },
    enabled: !!propertyId,
    staleTime: 15_000, // Phones and other accounts change this data; recheck soon
    refetchOnMount: true,
    placeholderData: keepPreviousData, // Keep showing current data while refetching (prevents calendar flash/disappear)
  });
};

export const useMarkTaskComplete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, propertyId }: { taskId: string; propertyId: string }) => {
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({ 
          status: "completed", 
          completed_at: new Date().toISOString() 
        })
        .eq("id", taskId);

      if (error) throw error;

      await scheduleNextPreventativeMaintenanceOnComplete(taskId);

      return { propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["my-preventative-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["cleaner-past-cleanings"] });
      queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
    },
  });
};

export const useGenerateCleaningTasks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      propertyId, 
      reservations,
      assignmentMode = "auto",
      selectedCleanerId = null,
    }: { 
      propertyId: string; 
      reservations: Reservation[];
      assignmentMode?: "auto" | "self" | "unassigned" | "cleaner";
      selectedCleanerId?: string | null;
    }) => {
      // Get existing tasks for this property
      const { data: existingTasks, error: fetchError } = await supabase
        .from("cleaning_tasks")
        .select("*")
        .eq("property_id", propertyId);

      if (fetchError) {
        console.error("[useCleaningTasks] Error fetching existing tasks:", fetchError);
        // TEMPORARY: Return empty array instead of throwing to stabilize flow
        return { created: 0 };
      }

      const existingReservationIds = new Set(
        existingTasks?.map((t) => t.reservation_id) || []
      );

      // Filter reservations that don't have tasks yet AND have future check-outs
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const newReservations = reservations.filter((r) => {
        // Skip if task already exists
        if (existingReservationIds.has(r.id)) return false;
        
        // Only create tasks for future or today check-outs
        const checkoutDate = new Date(r.end_date);
        checkoutDate.setHours(0, 0, 0, 0);
        return checkoutDate >= today;
      });

      if (newReservations.length === 0) {
        return { created: 0 };
      }

      // Fetch automation rules for this property (ordered by created_at)
      const { data: rules } = await supabase
        .from("checklist_rules")
        .select("id, checklist_id, rule_type, rule_value")
        .eq("property_id", propertyId)
        .order("created_at", { ascending: true });

      // Get the property's default_checklist_id and default_cleaner_id
      const { data: property } = await supabase
        .from("properties")
        .select("default_checklist_id, default_cleaner_id")
        .eq("id", propertyId)
        .single();

      let fallbackChecklistId = property?.default_checklist_id;

      // Resolve assigned_type and cleaner_id for new tasks.
      // Never stamp cleaner_id without assigned_type=cleaner (causes ghost jobs on worker schedule).
      // Do not auto-assign just because the property has exactly one cleaner — Assign is explicit.
      let defaultAssignedType: "owner" | "cleaner" | "unassigned" = "unassigned";
      let defaultCleanerId: string | null = null;
      if (assignmentMode === "self") {
        defaultAssignedType = "owner";
      } else if (assignmentMode === "unassigned") {
        defaultAssignedType = "unassigned";
      } else if (assignmentMode === "cleaner" && selectedCleanerId) {
        defaultAssignedType = "cleaner";
        defaultCleanerId = selectedCleanerId;
      } else {
        // auto: only pre-assign when property has an explicit default_cleaner_id
        const defaultCleaner = (property as { default_cleaner_id?: string | null })?.default_cleaner_id;
        if (defaultCleaner) {
          defaultAssignedType = "cleaner";
          defaultCleanerId = defaultCleaner;
        }
      }

      // Fallback to first checklist if no default
      if (!fallbackChecklistId) {
        const { data: checklists } = await supabase
          .from("checklists")
          .select("id")
          .eq("property_id", propertyId)
          .order("created_at", { ascending: true })
          .limit(1);
        
        fallbackChecklistId = checklists?.[0]?.id || null;
      }

      // Create tasks for new reservations with rule-based checklist assignment
      const tasksToInsert = newReservations.map((reservation) => {
        // Step A: Calculate stay length
        const stayLength = calculateNights(reservation.start_date, reservation.end_date);
        
        // Step B: Calculate gap days to next booking
        const gapDays = calculateGapDays(reservation.end_date, reservations);
        
        // Step C: Get checkout day of week
        const checkoutDayOfWeek = getDayOfWeek(reservation.end_date);
        
        // Step D: Evaluate rules in order - first match wins
        let assignedChecklistId = fallbackChecklistId;
        let assignmentReason = "Default checklist was applied";
        
        if (rules && rules.length > 0) {
          for (const rule of rules) {
            if (evaluateRule(rule as ChecklistRule, stayLength, gapDays, checkoutDayOfWeek)) {
              assignedChecklistId = rule.checklist_id;
              assignmentReason = formatRuleReason(rule as ChecklistRule);
              break; // First matching rule wins
            }
          }
        }

        return {
          property_id: propertyId,
          reservation_id: reservation.id,
          status: "scheduled" as const,
          scheduled_date: reservation.end_date,
          checklist_id: assignedChecklistId,
          assignment_reason: assignmentReason,
          assignment_type: "auto",
          cleaner_id: defaultCleanerId,
          assigned_type: defaultAssignedType,
        };
      });

      const { error: insertError } = await supabase
        .from("cleaning_tasks")
        .insert(tasksToInsert);

      if (insertError) throw insertError;

      return { created: newReservations.length };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", variables.propertyId] });
    },
  });
};

export const useUpdateTaskChecklist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      taskId, 
      propertyId, 
      checklistId 
    }: { 
      taskId: string; 
      propertyId: string; 
      checklistId: string | null;
    }) => {
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({ 
          checklist_id: checklistId,
          assignment_reason: checklistId ? "Owner manually changed checklist" : null,
        })
        .eq("id", taskId);

      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
    },
  });
};

export const useAssignTaskCleaner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      taskId, 
      propertyId, 
      cleanerId,
    }: { 
      taskId: string; 
      propertyId: string; 
      cleanerId: string | null;
    }) => {
      const updatePayload: Record<string, unknown> = {
        cleaner_id: cleanerId ?? null,
        assignment_reason: cleanerId
          ? "Owner manually assigned cleaner"
          : null,
      };
      const { error } = await supabase
        .from("cleaning_tasks")
        .update(updatePayload)
        .eq("id", taskId);

      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
    },
  });
};

export const useCreateSingleTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      reservationId,
      checklistId,
      cleanerId,
      assigneeUserId,
      assignedType,
      assignments,
      includeInventoryCheck,
      coordinatorUserId,
      scheduledDate,
      scheduledStartAt,
      scheduledEndAt,
      overrideCheckOutTime,
      overrideCheckInTime,
      notes,
    }: {
      propertyId: string;
      reservationId: string;
      checklistId: string | null;
      cleanerId: string | null;
      assigneeUserId?: string | null;
      /** owner = self, team = team member, cleaner = cleaner, unassigned */
      assignedType?: "owner" | "cleaner" | "unassigned" | "team";
      /** When set, multiple checklists with per-checklist assignee; task row keeps single assignee null. */
      assignments?: Array<{ checklistId: string; assignedType: "owner" | "team" | "cleaner"; cleanerId: string | null; assigneeUserId: string | null }>;
      includeInventoryCheck?: boolean;
      coordinatorUserId?: string | null;
      scheduledDate: string;
      scheduledStartAt?: string | null;
      scheduledEndAt?: string | null;
      overrideCheckOutTime?: string | null;
      overrideCheckInTime?: string | null;
      notes?: string | null;
    }) => {
      const useAssignments = assignments && assignments.length > 0;
      const firstAssignment = useAssignments ? assignments[0] : null;
      const firstChecklistId = useAssignments ? assignments[0].checklistId : checklistId;
      const payload: Record<string, unknown> = {
        property_id: propertyId,
        reservation_id: reservationId,
        checklist_id: firstChecklistId,
        cleaner_id: useAssignments
          ? firstAssignment!.assignedType === "cleaner"
            ? firstAssignment!.cleanerId
            : null
          : assignedType === "owner" || assignedType === "team"
            ? null
            : cleanerId,
        assignee_user_id: useAssignments
          ? firstAssignment!.assignedType === "cleaner"
            ? null
            : firstAssignment!.assigneeUserId
          : assignedType === "cleaner"
            ? null
            : (assigneeUserId ?? null),
        coordinator_user_id: coordinatorUserId ?? null,
        scheduled_date: scheduledDate,
        status: "scheduled",
        assignment_type: "manual",
        assignment_reason: "Turnover task",
        notes: notes ?? null,
        include_inventory_check: includeInventoryCheck ?? true,
      };
      if (!useAssignments && assignedType != null) payload.assigned_type = assignedType;
      if (useAssignments && firstAssignment) payload.assigned_type = firstAssignment.assignedType;
      if (overrideCheckOutTime != null) payload.override_check_out_time = overrideCheckOutTime;
      if (overrideCheckInTime != null) payload.override_check_in_time = overrideCheckInTime;
      if (scheduledStartAt) {
        payload.scheduled_start_at = scheduledStartAt;
        payload.scheduled_end_at =
          scheduledEndAt ?? new Date(new Date(scheduledStartAt).getTime() + 2 * 60 * 60 * 1000).toISOString();
      }
      let { data, error } = await supabase
        .from("cleaning_tasks")
        .insert(payload)
        .select("*, checklists(id, name)")
        .single();

      if (error && useAssignments && firstAssignment?.assignedType === "team") {
        const fallback = {
          ...payload,
          cleaner_id: null,
          assignee_user_id: null,
          assigned_type: "unassigned",
        };
        const retry = await supabase
          .from("cleaning_tasks")
          .insert(fallback)
          .select("*, checklists(id, name)")
          .single();
        data = retry.data;
        error = retry.error;
      }

      if (error) throw error;
      const taskId = (data as { id: string }).id;
      if (useAssignments) {
        await supabase.from("cleaning_task_checklist_assignments").insert(
          assignments.map((a) => ({
            cleaning_task_id: taskId,
            checklist_id: a.checklistId,
            assigned_type: a.assignedType,
            cleaner_id: a.cleanerId,
            assignee_user_id: a.assigneeUserId,
          }))
        );
      }
      return { propertyId, task: data };
    },
    onSuccess: (data) => {
      // Optimistic: add new task to cache so it appears instantly without refresh
      if (data.task) {
        const taskWithRelations = {
          ...data.task,
          checklists: (data.task as { checklists?: unknown }).checklists ?? null,
          reservations: (data.task as { reservations?: unknown }).reservations ?? null,
        } as CleaningTaskWithRelations;
        queryClient.setQueryData<CleaningTaskWithRelations[]>(
          ["cleaning-tasks", data.propertyId],
          (old) => {
            const next = old ? [...old, taskWithRelations] : [taskWithRelations];
            return next.sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));
          }
        );
      }
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["reservations", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["company-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task"] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["needs-attention"] });
      queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      queryClient.refetchQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.refetchQueries({ queryKey: ["reservations", data.propertyId] });
    },
  });
};

/** Create a standalone manual task (no reservation). */
export const useCreateManualTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      scheduledDate,
      scheduledStartAt,
      scheduledEndAt,
      checklistId,
      cleanerId,
      assigneeUserId,
      assignedType,
      assignments,
      includeInventoryCheck,
      coordinatorUserId,
      notes,
      guestCount,
    }: {
      propertyId: string;
      scheduledDate: string;
      scheduledStartAt?: string | null;
      scheduledEndAt?: string | null;
      checklistId: string | null;
      cleanerId: string | null;
      assigneeUserId?: string | null;
      assignedType?: "owner" | "cleaner" | "unassigned" | "team";
      assignments?: Array<{ checklistId: string; assignedType: "owner" | "team" | "cleaner"; cleanerId: string | null; assigneeUserId: string | null }>;
      includeInventoryCheck?: boolean;
      coordinatorUserId?: string | null;
      notes?: string | null;
      guestCount?: number | null;
    }) => {
      const useAssignments = assignments && assignments.length > 0;
      const firstAssignment = useAssignments ? assignments[0] : null;
      const firstChecklistId = useAssignments ? assignments[0].checklistId : checklistId;
      const payload: Record<string, unknown> = {
        property_id: propertyId,
        reservation_id: null,
        checklist_id: firstChecklistId,
        cleaner_id: useAssignments
          ? firstAssignment!.assignedType === "cleaner"
            ? firstAssignment!.cleanerId
            : null
          : assignedType === "owner" || assignedType === "team"
            ? null
            : cleanerId,
        assignee_user_id: useAssignments
          ? firstAssignment!.assignedType === "cleaner"
            ? null
            : firstAssignment!.assigneeUserId
          : assignedType === "cleaner"
            ? null
            : (assigneeUserId ?? null),
        coordinator_user_id: coordinatorUserId ?? null,
        scheduled_date: scheduledDate,
        status: "scheduled",
        assignment_type: "manual",
        assignment_reason: "Manual task",
        notes: notes ?? null,
        include_inventory_check: includeInventoryCheck ?? true,
      };
      if (!useAssignments && assignedType != null) payload.assigned_type = assignedType;
      if (useAssignments && firstAssignment) payload.assigned_type = firstAssignment.assignedType;
      if (scheduledStartAt) payload.scheduled_start_at = scheduledStartAt;
      if (scheduledEndAt) payload.scheduled_end_at = scheduledEndAt;
      if (guestCount !== undefined) payload.guest_count = guestCount;
      let { data, error } = await supabase
        .from("cleaning_tasks")
        .insert(payload)
        .select("*, checklists(id, name)")
        .single();

      if (error && useAssignments && firstAssignment?.assignedType === "team") {
        const fallback = {
          ...payload,
          cleaner_id: null,
          assignee_user_id: null,
          assigned_type: "unassigned",
        };
        const retry = await supabase
          .from("cleaning_tasks")
          .insert(fallback)
          .select("*, checklists(id, name)")
          .single();
        data = retry.data;
        error = retry.error;
      }

      if (error) throw error;
      const taskId = (data as { id: string }).id;
      if (useAssignments) {
        await supabase.from("cleaning_task_checklist_assignments").insert(
          assignments!.map((a) => ({
            cleaning_task_id: taskId,
            checklist_id: a.checklistId,
            assigned_type: a.assignedType,
            cleaner_id: a.cleanerId,
            assignee_user_id: a.assigneeUserId,
          }))
        );
      }
      return { propertyId, task: data };
    },
    onSuccess: (data) => {
      if (data.task) {
        const taskWithRelations = {
          ...data.task,
          checklists: (data.task as { checklists?: unknown }).checklists ?? null,
        } as CleaningTaskWithRelations;
        queryClient.setQueryData<CleaningTaskWithRelations[]>(
          ["cleaning-tasks", data.propertyId],
          (old) => {
            const next = old ? [...old, taskWithRelations] : [taskWithRelations];
            return next.sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));
          }
        );
      }
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["reservations", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["company-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task"] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["needs-attention"] });
      queryClient.refetchQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.refetchQueries({ queryKey: ["reservations", data.propertyId] });
    },
  });
};

/** Update scheduled time for a task (calendar drag). Owner or team admin. Invalidates schedule-related queries. */
export const useUpdateCleaningTaskTime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      propertyId,
      startAt,
      endAt,
    }: {
      taskId: string;
      propertyId: string;
      startAt: string;
      endAt: string;
    }) => {
      const { data, error } = await supabase.rpc("update_cleaning_task_time", {
        p_task_id: taskId,
        p_start_at: startAt,
        p_end_at: endAt,
      });
      if (error) throw error;
      return { propertyId, task: data };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["reservations", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["company-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task"] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
    },
  });
};
